import { z } from 'zod';
import { CompanyPackagesSchema } from '../company/company.contract';
import { HotTopicsPeriod } from '../platform-settings/platform-settings.enums';
import { HotTopicWithRelatedSchema } from '../platform-settings/platform-settings.schema';
import { HOT_TOPIC_COUNT } from './dashboard.default';

export const CurrentWeekSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});

export const ConversationsCountSchema = z.object({
  singleQuestionsCount: z.number(),
  groupConversationsCount: z.number(),
});

export const MostFrequentQuestionInfoSchema = z.object({
  question: z.string(),
  frequency: z.number(),
});

export const ConversationalAnalysisSchema = z.object({
  totalQuestions: z.number(),
  uniqueUsers: z.number(),
  percentageChange: z.number(),
  changeDirection: z.union([
    z.literal('increased'),
    z.literal('decreased'),
    z.literal('same'),
  ]),
  mostActiveDay: z.string(),
  mostActiveStartHour: z.number(),
  mostActiveEndHour: z.number(),
  maxActivity: z.number(),
  hotTopics: z.array(HotTopicWithRelatedSchema).default([]),
  hotTopicsEnabled: z.boolean().default(false),
  hotTopicsPeriod: z
    .enum(
      Object.values(HotTopicsPeriod) as [HotTopicsPeriod, ...HotTopicsPeriod[]],
    )
    .default(HotTopicsPeriod.DAILY),
  hotTopicsDisplayLimit: z.number().default(HOT_TOPIC_COUNT),
  timeSavedEnabled: z.boolean().default(false),
  mostFrequentQuestionInfo: MostFrequentQuestionInfoSchema,
});

export const leadsGenAnalysisSchema = z.object({
  leadsGenerated: z.number().nullable(),
});

export const AccuracyTestSchema = z.object({
  _id: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  accuracy: z.number(),
  isVisible: z.boolean().optional().default(true),
  createdAt: z.string(),
});

export const UserEngagementMetricsSchema = z.object({
  aiExplorers: z.number().default(0), // * 1 question
  aiPowerUsers: z.number().default(0), // * 3+ questions
  aiSuperFans: z.number().default(0), // * 10+ questions
});

export const DashboardSchema = z.object({
  botName: z.string(),
  botLogo: z.string().optional(),
  unFactCheckedItemsCount: z.number(),
  totalConversationCount: z.number(),
  currentWeek: CurrentWeekSchema,
  conversationsCount: ConversationsCountSchema,
  conversationalAnalysis: ConversationalAnalysisSchema,
  didYouKnow: z.string(),
  leadsGenAnalysis: leadsGenAnalysisSchema.nullable(),
  aiAccuracy: z.object({
    accuracy: z.number(),
    date: z.string(),
  }),
  certified: z.boolean(),
  packages: CompanyPackagesSchema.optional(),
  accuracyTests: AccuracyTestSchema.array().default([]),
  aiAccuracyBenchmark: z.object({
    enabled: z.boolean(),
    value: z.number(),
  }),
  aiChartSettings: z.object({
    showSubjective: z.boolean(),
    showCombined: z.boolean(),
    showObjective: z.boolean(),
  }),
  featureAccesses: z.object({
    leadsInterestedCard: z.boolean().default(false),
    researchQuestionsCard: z.boolean().default(false),
    workflowAutomatedCard: z.boolean().default(false),
    handoverToHumanCard: z.boolean().default(false),
    csatCard: z.boolean().default(false),
    ticketDeflectionCard: z.boolean().default(false),
  }),
  userEngagementMetrics: UserEngagementMetricsSchema.optional(),
  // * score/configured are computed by the backend controller. upCount/downCount/
  // * noClickCount carry the raw counts from the core→backend response so Zod does not
  // * strip them before the controller computes the score; they never appear in the
  // * public HTTP response (the controller overrides csat to { score, configured }).
  csat: z.object({
    score: z.number().min(0).max(100).default(0),
    configured: z.boolean().default(false),
    upCount: z.number().optional(),
    downCount: z.number().optional(),
    noClickCount: z.number().optional(),
  }),
  // * ratio is the only field the frontend reads. deflectedCount/escalatedCount/
  // * noButtonPressCount are present because the DashboardSchema is also used to
  // * parse the core→backend internal API response; without them, Zod would strip
  // * the counts before the backend controller can compute ratio from them.
  // * The controller explicitly overrides ticketDeflection to { ratio } before
  // * returning, so these fields never appear in the public HTTP response.
  ticketDeflection: z.object({
    ratio: z.number().min(0).max(100).default(0),
    deflectedCount: z.number().optional(),
    escalatedCount: z.number().optional(),
    noButtonPressCount: z.number().optional(),
  }),
});

export const DashboardRequestSchema = z.object({
  leadGenerationBlockId: z.string().nullable(),
  companyPackages: CompanyPackagesSchema,
  shouldComputeTicketDeflection: z.boolean(),
  // * CSAT block ids live in Core app_config now; the backend only signals the window
  // * (ISO strings) and whether to compute (feature access on + override off).
  shouldComputeCsat: z.boolean(),
  csatFrom: z.string().nullable(),
  csatTo: z.string().nullable(),
});
